import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import './index.scss';

export type VideoSource =
  | { kind: 'youtube'; id: string }
  | { kind: 'file'; src: string };

export interface VideoRequest {
  source: VideoSource;
  title: string;
  kicker?: string;
}

interface VideoModalContextValue {
  open: (req: VideoRequest) => void;
}

const VideoModalContext = createContext<VideoModalContextValue>({ open: () => undefined });

export const useVideoModal = () => useContext(VideoModalContext);

const youtubeEmbed = (id: string) =>
  `https://www.youtube.com/embed/${id}?rel=0&autoplay=1`;

export const VideoModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [req, setReq] = useState<VideoRequest | null>(null);

  const open = useCallback((r: VideoRequest) => {
    setReq(r);
    const d = dialogRef.current;
    if (d && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    }
  }, []);

  const close = useCallback(() => {
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
    const d = dialogRef.current;
    if (!d || !d.open || d.classList.contains('is-closing')) return;
    // play the exit animation, then actually close
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    d.classList.add('is-closing');
    window.setTimeout(() => {
      d.classList.remove('is-closing');
      d.close();
      setReq(null);
    }, reduced ? 0 : 200);
  }, []);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const onClose = () => setReq(null);
    // route Esc through close() so it animates too
    const onCancel = (e: Event) => { e.preventDefault(); close(); };
    d.addEventListener('close', onClose);
    d.addEventListener('cancel', onCancel);
    return () => {
      d.removeEventListener('close', onClose);
      d.removeEventListener('cancel', onCancel);
    };
  }, [close]);

  return (
    <VideoModalContext.Provider value={{ open }}>
      {children}
      <dialog
        className="modal"
        ref={dialogRef}
        aria-labelledby="modalTitle"
        onClick={(e) => { if (e.target === dialogRef.current) close(); }}
      >
        <div className="modal-in">
          <div className="modal-head">
            <div>
              <p>{req?.kicker ?? ''}</p>
              <h3 id="modalTitle">{req?.title ?? ''}</h3>
            </div>
            <button className="close" onClick={close} aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <figure className="demo-fig is-ready">
            {req?.source.kind === 'youtube' && (
              <iframe
                src={youtubeEmbed(req.source.id)}
                title={req.title}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
              />
            )}
            {req?.source.kind === 'file' && (
              <video ref={videoRef} src={req.source.src} playsInline controls autoPlay />
            )}
          </figure>
        </div>
      </dialog>
    </VideoModalContext.Provider>
  );
};
